import { MyFieldNotesList, MY_FIELD_NOTES_FETCH_LIMIT } from "@/components/MyFieldNotesList";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FIELD_NOTE_CORRECTION_TYPES, isFieldNoteFormValid } from "@/lib/fieldNotes";
import { useFieldNotesViewModel } from "@/pages/dashboard/field-notes/useFieldNotesViewModel";

export function FieldNotesView() {
  const {
    user,
    isAdmin,
    fieldNoteForm,
    setFieldNoteForm,
    fieldNoteSubmitError,
    fieldNoteSaved,
    fieldNotesData,
    handleFieldNoteSubmit,
    nowMs,
  } = useFieldNotesViewModel();

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3 [&>*]:min-w-0">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>New Field Note</CardTitle>
                  <CardDescription>Submit a standalone field note not tied to a timeclock punch.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <div className="grid gap-1">
                      <Label className="text-sm">Type of Correction <span className="text-destructive">*</span></Label>
                      <Select
                        value={fieldNoteForm.correction_type}
                        onValueChange={(value) => setFieldNoteForm({ ...fieldNoteForm, correction_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select correction type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {FIELD_NOTE_CORRECTION_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-sm">Reason for Correction <span className="text-destructive">*</span></Label>
                      <Textarea
                        placeholder="Write all details about this correction, such as the incorrect time punch, correct time punch, timezone, or the ST/OT related to it."
                        value={fieldNoteForm.reason_for_correction}
                        onChange={(e) => setFieldNoteForm({ ...fieldNoteForm, reason_for_correction: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-sm">Additional Note</Label>
                      <Textarea
                        placeholder="Enter any additional details..."
                        value={fieldNoteForm.additional_note}
                        onChange={(e) => setFieldNoteForm({ ...fieldNoteForm, additional_note: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={() => { void handleFieldNoteSubmit(); }}
                      disabled={!isFieldNoteFormValid(fieldNoteForm)}
                    >
                      Submit Field Note
                    </Button>
                    {fieldNoteSubmitError && (
                      <Alert role="alert" className="border-destructive/20 bg-destructive/10 text-destructive">
                        <AlertTitle>Field note not saved</AlertTitle>
                        <div className="text-sm">{fieldNoteSubmitError}</div>
                      </Alert>
                    )}
                    {fieldNoteSaved && (
                      <Alert role="status" aria-live="polite" className="border-success/20 bg-success/10 text-success">
                        <AlertTitle>Field note saved</AlertTitle>
                        <div className="text-sm">
                          {fieldNoteSaved.direct
                            ? "Field note sent directly to the server (local storage was unavailable)."
                            : "This note is stored on this device and will sync automatically."}
                        </div>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>My Field Notes</CardTitle>
                  <CardDescription>History of submitted field notes and sync status.</CardDescription>
                </CardHeader>
                <CardContent>
                  <MyFieldNotesList
                    // Own submissions only (backend personal path + admin safety filter).
                    notes={
                      isAdmin
                        ? (fieldNotesData ?? []).filter((note) => note.user_id === user?.id)
                        : (fieldNotesData ?? [])
                    }
                    nowMs={nowMs()}
                    fetchLimit={MY_FIELD_NOTES_FETCH_LIMIT}
                  />
                </CardContent>
              </Card>

    </div>
  );
}
